import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, Check, ChevronLeft, Loader2 } from "lucide-react";
import { api, useAuth } from "@/hooks/useAuth";

/**
 * VoiceProfileAB — A/B/C audition for Nishant voice character.
 * Route: /app/talk/voices/ab
 *
 * Pipeline untouched. This endpoint only controls `voice_settings` sent to
 * ElevenLabs. The chosen profile is persisted in `kalki_config.voice.profile`.
 *
 * Variants:
 *   A — ORIGINAL NISHANT  (current baseline)
 *   B — NISHANT FULLER    (10-15% thicker chest / fuller presence)
 *   C — MATURE KALKI ~40  (38-45 yrs: thick, calm, cool, naturally authoritative)
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INDIGO = "#5D00FF";
const FUCHSIA = "#FF2E9A";
const LIME = "#CFFF04";
const GRAPHITE = "#1C1C1C";

const AB_SCRIPT = "Something in your journey is becoming clearer to me. The future is not asking you to rush. Your timing shows an opportunity ahead, but what you choose to build now will determine how beautifully you meet it. Tell me — what do you truly want this next phase of your life to become?";

const PROFILES = [
  { id: "original",     name: "A · ORIGINAL NISHANT", blurb: "Current baseline — warm, clear, conversational." },
  { id: "fuller",       name: "B · NISHANT FULLER",   blurb: "10–15% thicker, fuller chest, deeper presence." },
  { id: "mature_kalki", name: "C · MATURE KALKI ~40", blurb: "38–45 yrs: thick, calm, cool, naturally authoritative." },
];

export default function VoiceProfileAB() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [committing, setCommitting] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [error, setError] = useState("");

  const audioRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate("/app", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/kalki/voices");
        setCfg(r.data);
      } catch (e) { setError("Could not load voice config."); }
    })();
  }, []);

  const ensureAudio = () => {
    if (!audioRef.current) {
      const el = new Audio();
      el.preload = "auto";
      el.playsInline = true;
      el.volume = 1;
      audioRef.current = el;
    }
    return audioRef.current;
  };

  const voiceId = cfg?.active_voice_id;
  const currentProfile = cfg?.profile || "original";

  const previewProfile = async (profile) => {
    if (!voiceId || busyId) return;
    setError("");
    setBusyId(profile);
    try {
      const el = ensureAudio();
      try { el.pause(); } catch (_) {}
      const r = await fetch(`${BACKEND_URL}/api/kalki/voices/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ voice_id: voiceId, text: AB_SCRIPT, profile }),
      });
      if (!r.ok) throw new Error(`TTS ${r.status}`);
      const type = r.headers.get("content-type") || "";
      const blob = await r.blob();
      if (!type.startsWith("audio/") || blob.size === 0) throw new Error("empty blob");
      const url = URL.createObjectURL(blob);
      el.src = url;
      el.onplaying = () => setPlayingId(profile);
      el.onended = () => { setPlayingId(null); try { URL.revokeObjectURL(url); } catch (_) {} };
      el.onerror = () => setPlayingId(null);
      await el.play();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusyId(null);
    }
  };

  const commitProfile = async (profile) => {
    try {
      setCommitting(profile);
      setError("");
      const r = await api.post("/kalki/voices/select", { profile });
      setCfg((c) => ({ ...c, profile: r.data?.profile || profile }));
      setCommitted(profile);
      setTimeout(() => setCommitted(null), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not save profile.");
    } finally {
      setCommitting(null);
    }
  };

  return (
    <div className="min-h-[100dvh] text-white" style={{ background: GRAPHITE }} data-testid="voice-ab">
      <div className="max-w-[720px] mx-auto px-6 py-8">
        <button onClick={() => navigate("/app/talk")} className="inline-flex items-center gap-1 text-[11px] tracking-[0.3em] opacity-70 hover:opacity-100">
          <ChevronLeft className="w-3 h-3" /> BACK TO TALK
        </button>

        <h1 className="text-[14px] tracking-[0.4em] opacity-80 mt-6">KALKI VOICE · A/B/C AUDITION</h1>
        <p className="text-[12px] opacity-55 mt-1">
          Same base voice. Three vocal characters tuned via `voice_settings`. The target: a mature
          38–45 year-old KALKI voice — thick, calm, cool, intelligent, naturally authoritative.
        </p>

        {cfg && (
          <div className="mt-3 text-[11px] opacity-60 font-mono">
            voice_id: {cfg.active_voice_id} · active profile: <span style={{ color: LIME }}>{currentProfile}</span>
          </div>
        )}

        <blockquote
          className="mt-6 rounded-xl px-4 py-3 text-[13px] leading-[1.6]"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#D9D9E0" }}
          data-testid="ab-script"
        >
          {AB_SCRIPT}
        </blockquote>

        <ul className="mt-6 space-y-3">
          {PROFILES.map((p) => {
            const isActive = p.id === currentProfile;
            const isPlaying = playingId === p.id;
            const isLoading = busyId === p.id;
            const isCommitting = committing === p.id;
            const justCommitted = committed === p.id;
            return (
              <li
                key={p.id}
                data-testid={`ab-row-${p.id}`}
                className="flex items-center gap-4 rounded-2xl px-4 py-4"
                style={{
                  background: isActive ? `${INDIGO}18` : "rgba(255,255,255,0.04)",
                  border: `1px solid ${isActive ? INDIGO : "rgba(255,255,255,0.08)"}`,
                }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium tracking-[0.14em]">{p.name}</span>
                    {isActive && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full tracking-[0.28em]"
                            style={{ color: LIME, border: `1px solid ${LIME}66`, background: `${LIME}10` }}>
                        ACTIVE
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] opacity-70 mt-0.5">{p.blurb}</div>
                </div>

                <button
                  data-testid={`ab-play-${p.id}`}
                  onClick={() => previewProfile(p.id)}
                  disabled={isLoading || !voiceId}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition disabled:opacity-50"
                  style={{
                    background: isPlaying ? FUCHSIA : "rgba(255,255,255,0.08)",
                    border: `1px solid ${isPlaying ? FUCHSIA : "rgba(255,255,255,0.18)"}`,
                  }}
                  aria-label={`Play ${p.name}`}
                >
                  {isLoading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : isPlaying
                      ? <Pause className="w-4 h-4" />
                      : <Play className="w-4 h-4 translate-x-[1px]" />}
                </button>

                <button
                  data-testid={`ab-select-${p.id}`}
                  onClick={() => commitProfile(p.id)}
                  disabled={isCommitting || isActive}
                  className="shrink-0 rounded-full px-4 py-2.5 text-[10px] tracking-[0.26em] font-medium disabled:opacity-50"
                  style={{
                    background: justCommitted ? LIME : isActive ? "rgba(255,255,255,0.04)" : "white",
                    color: isActive ? "rgba(255,255,255,0.65)" : GRAPHITE,
                    border: isActive ? "1px solid rgba(255,255,255,0.14)" : "1px solid white",
                  }}
                >
                  {isCommitting
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : justCommitted
                      ? <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" /> SAVED</span>
                      : isActive ? "IN USE" : "USE THIS"}
                </button>
              </li>
            );
          })}
        </ul>

        {error && (
          <div className="mt-6 rounded-xl px-4 py-3 text-[12px]"
               style={{ background: `${FUCHSIA}1a`, border: `1px solid ${FUCHSIA}66`, color: "#FFE1EC" }}>
            {error}
          </div>
        )}

        <p className="text-[10px] opacity-40 mt-10 tracking-[0.2em]">
          Pipeline unchanged. Only `kalki_config.voice.profile` is updated. Next `/api/kalki/tts` picks it up.
        </p>
      </div>
    </div>
  );
}
